import axios from 'axios';
import base_url from '../baseUrls';
import {getAiCallInspectionLocation} from './aiCallSession';

/**
 * Desktop notifications for incoming AI calls: a native Windows / macOS notification with Accept
 * and Decline buttons, raised by `public/ai-call-sw.js`.
 *
 * Only an open portal tab raises it, and only while the agent is Available: the tab that gets
 * the prompt over its socket hands it to the service worker, which shows it. With no portal tab
 * open there is no notification.
 */

const SERVICE_WORKER_PATH = '/ai-call-sw.js';

/** Shared with the service worker: an accepted call waiting for a tab to ask for its property. */
const ACCEPTED_CALL_CACHE = 'ipermit-ai-call';
const ACCEPTED_CALL_KEY = '/__ai-call-accepted';
/** Older than this, the agent has long since dealt with the call some other way. */
const ACCEPTED_CALL_MAX_AGE_MS = 2 * 60 * 1000;

/** Message the service worker sends a tab after Accept on a call with no job attached. */
export const AI_CALL_ACCEPTED_FROM_NOTIFICATION = 'ai-call-accepted-from-notification';

export function aiCallNotificationsSupported() {
  return (
    typeof window !== 'undefined'
    && window.isSecureContext
    && 'serviceWorker' in navigator
    && 'Notification' in window
  );
}

/** `granted`, `denied`, `default` (not asked yet), or `unsupported`. */
export function getAiCallNotificationPermission() {
  return aiCallNotificationsSupported() ? Notification.permission : 'unsupported';
}

let registrationPromise = null;

/**
 * Registers the AI call service worker, once per page. Its settings ride on the script URL - the
 * API base and the inspection page an accepted call opens - because that is the only state the
 * browser keeps for a worker it has stopped and restarted.
 *
 * @returns {Promise<ServiceWorkerRegistration|null>}
 */
export function registerAiCallServiceWorker() {
  if (!aiCallNotificationsSupported()) {
    return Promise.resolve(null);
  }
  if (!registrationPromise) {
    const inspection = getAiCallInspectionLocation('__ID__');
    const params = new URLSearchParams({
      api: base_url.api,
      inspection: inspection ? `${inspection.pathname}${inspection.search}` : '',
    });
    registrationPromise = navigator.serviceWorker
      .register(`${SERVICE_WORKER_PATH}?${params.toString()}`, {scope: '/'})
      .then(() => navigator.serviceWorker.ready)
      .catch((error) => {
        console.error('AI call service worker registration failed:', error);
        registrationPromise = null;
        return null;
      });
  }
  return registrationPromise;
}

async function postToServiceWorker(message) {
  const registration = await registerAiCallServiceWorker();
  const worker = registration?.active || registration?.waiting || registration?.installing;
  worker?.postMessage(message);
}

/**
 * Raises the desktop notification for an `ai-call-incoming` payload. A no-op without permission,
 * or when this prompt's notification is already up (another of the agent's tabs raised it).
 */
export function showIncomingCallNotification(data) {
  if (getAiCallNotificationPermission() !== 'granted' || !data?.answerToken) {
    return;
  }
  // The AI's summary can run long and nothing on the notification uses it.
  const {summary, ...call} = data;
  postToServiceWorker({type: 'show-incoming', call});
}

/** Takes the prompt's notification down; without a token, any incoming-call notification. */
export function closeIncomingCallNotification(answerToken) {
  if (!aiCallNotificationsSupported()) {
    return;
  }
  postToServiceWorker({type: 'close-incoming', answerToken: answerToken || undefined});
}

/** Replaces the prompt with the missed-call notice. */
export function showMissedCallNotification(answerToken) {
  if (getAiCallNotificationPermission() !== 'granted') {
    return;
  }
  postToServiceWorker({type: 'show-missed', answerToken: answerToken || undefined});
}

/**
 * A sample notification, so an agent can check that Windows Focus Assist, macOS Focus or the
 * browser's own settings are not hiding call alerts before a real call depends on it.
 */
export function showTestCallNotification() {
  if (getAiCallNotificationPermission() !== 'granted') {
    return;
  }
  postToServiceWorker({type: 'show-test'});
}

/**
 * Tells the backend the prompt reached this tab. It logs the time since the dialer handed the
 * call over, which is how a late or missing prompt gets noticed. Best-effort; needs no session.
 */
export function acknowledgeAiCallPrompt(answerToken, channel) {
  if (!answerToken) {
    return;
  }
  axios
    .post(`${base_url.api}ai/call/delivered`, {token: answerToken, channel})
    .catch(() => undefined);
}

/**
 * This tab is unloading. The service worker takes any prompt off screen once no portal tab is left,
 * since without one there is nothing behind the notification.
 */
export function notifyAiCallTabClosing() {
  if (!aiCallNotificationsSupported()) {
    return;
  }
  navigator.serviceWorker.controller?.postMessage({type: 'tab-closing'});
}

/**
 * Asks for notification permission. Browsers only show the question in response to a click, so
 * call this from one.
 *
 * @returns {Promise<'granted'|'denied'|'default'|'unsupported'>}
 */
export async function requestAiCallNotificationPermission() {
  if (!aiCallNotificationsSupported()) {
    return 'unsupported';
  }
  let permission = Notification.permission;
  if (permission === 'default') {
    try {
      permission = await Notification.requestPermission();
    } catch (error) {
      permission = Notification.permission;
    }
  }
  if (permission === 'granted') {
    await registerAiCallServiceWorker();
  }
  return permission;
}

/**
 * The call the agent accepted from a notification when it had no job attached, so this tab can
 * ask them for the property. Taken once: the first tab to read it owns it.
 *
 * @returns {Promise<object|null>} the incoming-call payload, or null.
 */
export async function takeCallAcceptedFromNotification() {
  if (typeof caches === 'undefined') {
    return null;
  }
  try {
    const cache = await caches.open(ACCEPTED_CALL_CACHE);
    const response = await cache.match(ACCEPTED_CALL_KEY);
    if (!response) {
      return null;
    }
    await cache.delete(ACCEPTED_CALL_KEY);
    const {call, at} = await response.json();
    if (!call || Date.now() - Number(at) > ACCEPTED_CALL_MAX_AGE_MS) {
      return null;
    }
    return call;
  } catch (error) {
    return null;
  }
}

/**
 * Listens for messages from the AI call service worker.
 * @returns {() => void} unsubscribe
 */
export function onAiCallServiceWorkerMessage(handler) {
  if (!aiCallNotificationsSupported()) {
    return () => undefined;
  }
  const listener = (event) => handler(event.data || {});
  navigator.serviceWorker.addEventListener('message', listener);
  return () => navigator.serviceWorker.removeEventListener('message', listener);
}
